import React, {CSSProperties, useCallback, useEffect, useRef, useState} from 'react';
import Box from '@mui/material/Box';
import Logger from '../lib/Logger';
import {createPropertyTween} from '../lib/three/tweens';
import {Tween} from "@tweenjs/tween.js";

interface WrapperProps {
    index: number,
    rootRef: React.RefObject<any>,
    children: React.ReactElement,
    onIntersected?: () => void,
    onHeightChanged?: (height: number) => void,
    observe?: boolean,
    isTopObserver?: boolean,
    logger: Logger,
}

function Wrapper(
        {
            index,
            rootRef,
            children,
            onIntersected,
            onHeightChanged,
            observe,
            isTopObserver,
            logger,
        }: WrapperProps) {

    const ref: React.ForwardedRef<HTMLDivElement> = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const height = ref.current?.clientHeight;
        // logger.trace("element has mounted. index: " + index + ". height: " + height);

        if (onHeightChanged && height !== undefined) {
            onHeightChanged(height)
        }

        return () => {
            // logger.trace("element has unmounted. index: " + index + ". height: " + height);
        }
    }, []);


    useEffect(() => {
        if (observe && ref.current) {
            logger.trace(`creating ${isTopObserver ? "top" : "bottom"} intersection observer. index: ` + index);

            const observer = new IntersectionObserver(
                    entries => {
                        for (const entry of entries) {
                            if (entry.isIntersecting) {
                                logger.trace(` ${isTopObserver ? "top" : "bottom"} observer  intersected the rootRef. index` + index);
                                if (onIntersected) {
                                    onIntersected();
                                }

                                // observer.disconnect();
                                break;
                            }
                        }
                    },
                    {
                        root: rootRef.current,
                        // rootMargin: uiStore.isMobile ? "0px 0px 8000px 0px" : "0px 0px 600px 0px",
                    }
            );
            observer.observe(ref.current);
            return () => {
                logger.trace(`disconnecting ${isTopObserver ? "top" : "bottom"} intersection observer. index: ` + index);
                observer.disconnect();
            }
        }

    }, [onIntersected, observe]);

    useEffect(() => {
        // logger.trace("creating resize observer. index: " + index);
        if (onHeightChanged && ref.current) {
            const observer = new ResizeObserver(() => {
                if (ref.current) {
                    const height = ref.current.clientHeight;
                    // logger.trace("element has changed size. index: " + index + ". height: " + height);
                    onHeightChanged(height)
                } else {
                    logger.trace("element has changed size but no ref available. index: " + index);
                }

            });
            observer.observe(ref.current);
            return () => observer.disconnect();
        }

    }, [onHeightChanged]);

    return React.cloneElement(children, {ref, observer: observe})
}

//@todo is dependent on ThreeScreen to run for the animation loop. fix this?


interface ListProps {
    id?: string,
    width: string | number,
    items: { id: string, index?: number }[], // you should replace 'any' with the actual type of items
    style?: React.CSSProperties,
    // children: React.ReactElement,
    withRenderMessage?: boolean,

    [rest: string]: any,
}

interface ItemInfo {
    height: number
    bottom?: number
    top?: number
}

export default function List(
        {
            id = undefined,
            width,
            items,
            style,
            children: Children,
            withRenderMessage,
            ...rest
        }: ListProps) {

    const loggerRef = useRef<Logger>();

    if (!loggerRef.current) {
        loggerRef.current = new Logger(id ? "List-" + id : "List");
    }

    const logger = loggerRef.current;

    const [startIndex, setStartIndex] = useState(0);
    const [scrollHeight, setScrollHeight] = useState(0);
    const [map] = useState<{ [key: string]: ItemInfo }>({});
    const [storedItemArray, setStoredItemArray] = useState(items);
    const [heightChangeMap] = useState<{ [key: string]: boolean }>({});
    const [overlayVisible, setOverlayVisible] = useState(false);

    const [locked, setLocked] = useState(true);

    const rootRef = useRef<HTMLDivElement>(null);

    const idMap = {};

    //@todo map needs to be cleaned when amount of items changes, and suddenly new item gets added
    items.forEach((item, i) => {
        if (!("id" in item)) {
            throw new Error("Item with index " + i + "has no id");
        }

        if ((item.id in idMap)) {
            throw new Error("Duplicate id's. An item with index " + i + " and " + item.id + " was already found in item array")
        }

        if (!(item.id in map)) {
            map[item.id] = {
                height: 0,
            };
        }

        //@todo make proper data model
        item.index = i;
    })

    if (storedItemArray !== items) {

    }

    useEffect(() => {
        if (items.length < storedItemArray.length) {
            logger.trace("items.length changed");
            refreshItemPositions();
        } else {
            for (const [i, item] of items.entries()) {
                if (item !== storedItemArray[i]) {
                    logger.trace("items array content changed");
                    refreshItemPositions();
                    break;
                }
            }
        }
        setStoredItemArray(items)
    }, [items, refreshItemPositions])


    const maxSliceSize = 50;
    //@todo the startIndex can sometimes be higher when less items then previous get passed to this component. needs more robust handling
    const sliceSize = Math.max(0, Math.min(items.length - startIndex, maxSliceSize));

    const hasReachedBeginning = startIndex === 0;
    const hasReachedEnd = !(startIndex + sliceSize < items.length);

    const minItemsHidden = 3;
    const topObservationIndex: number | boolean = !hasReachedBeginning && Math.min(minItemsHidden, items.length);
    const bottomObservationIndex: number | boolean = !hasReachedEnd && Math.max(sliceSize - minItemsHidden, 0);

    const observers = useRef<{ top: ItemInfo | false, bottom: ItemInfo | false }>({top: false, bottom: false});

    // if (typeof topObservationIndex !== 'boolean') {
    //     observers.current.top = map[items[startIndex + topObservationIndex]?.id]
    // } else {
    //     observers.current.top = false;
    // }
    // if (typeof bottomObservationIndex !== 'boolean') {
    //     observers.current.bottom = map[items[startIndex + bottomObservationIndex]?.id];
    // } else {
    //     observers.current.bottom = false;
    // }

    observers.current.top = typeof topObservationIndex !== 'boolean' && map[items[startIndex + topObservationIndex]?.id];
    observers.current.bottom = typeof bottomObservationIndex !== 'boolean' && map[items[startIndex + bottomObservationIndex]?.id];

    const itemCountBelowViewPort = 30;

    function onBottomIntersection() {
        logger.info("");
        logger.info("Bottom intersected.")
        logger.info("sliceSize: " + sliceSize)
        logger.info("items.length: " + items.length)
        logger.info("startIndex: " + startIndex)

        const newStartIndex = Math.min(Math.max(0, items.length - sliceSize), startIndex + itemCountBelowViewPort);

        logger.info("Setting startIndex to " + newStartIndex);
        logger.info("");
        setStartIndex(newStartIndex);

        if (!overlayVisible) {
            setOverlayVisible(true)
        }

    }

    function onTopIntersection() {
        logger.info("");
        logger.info("Top intersected.")
        logger.info("sliceSize: " + sliceSize)
        logger.info("items.length: " + items.length)
        logger.info("startIndex: " + startIndex)

        const newStartIndex = Math.max(0, startIndex - itemCountBelowViewPort);

        logger.trace("Setting startIndex to " + newStartIndex);
        logger.info("");
        setStartIndex(newStartIndex);

        if (!overlayVisible) {
            setOverlayVisible(true)
        }

    }

    useEffect(() => {

        if (overlayVisible) {
            logger.trace("refreshing overlay timeout");
            const timeoutId = setTimeout(() => {
                setOverlayVisible(false);
            }, 1000);

            return () => clearTimeout(timeoutId);
        }

    }, [overlayVisible, setOverlayVisible])

    let top = 0;
    // const mountMap = {};


    if (scrollHeight) {
        items.slice(0, startIndex).forEach((item, i) => {
            top += map[item.id].height;
            // if (i < startIndex + sliceSize) {
            //   top += map[i].height;
            // }
        });
    }

    function refreshItemPositions() {

        // const newHeightChangeMap = {}
        // emptyArray.forEach((_, i) => {
        //   newHeightChangeMap[i + startIndex + sliceSize] = true;
        // })
        // newHeightChangeMap
        logger.trace("refreshing item positions");
        logger.trace("total items: " + items.length);
        let totalHeight = 0;
        // setHeightChangeMap(newHeightChangeMap);

        items.forEach((item, i) => {
            totalHeight += map[item.id].height;
        });
        logger.trace("setting scrollHeight: " + totalHeight);

        setScrollHeight(totalHeight);
        if (!rootRef.current) {
            return;
        }

        const {
            scrollTop,
            clientHeight
        } = rootRef.current

        const target = totalHeight - clientHeight; //this is the bottom

        const distanceToBottom = totalHeight - scrollTop - clientHeight;
        let duration = 500;

        if (distanceToBottom > 1000) {
            duration = 0;
        }
        const buggyOffset = 100;

        if (locked) {
            scrollTo(target + buggyOffset, {duration});
        }

    }

    function calculateStartIndex() {
        let currentTop = 0;
        let firstLowerItemIndex = items.length - 1;

        if (!rootRef.current) {
            throw new Error("tried to calculate start index without rootRef present")
        }

        const {
            clientHeight
        } = rootRef.current;

        for (const [itemIndex, item] of items.entries()) {
            if (currentTop > rootRef.current.scrollTop + clientHeight) {
                logger.info("Found item below viewport while calculating start index. item with index %s is at %s from the top", itemIndex, currentTop)
                firstLowerItemIndex = itemIndex;
                break;
            }
            currentTop += map[item.id].height;
        }

        //-1 because the end index is the index of the last visible item
        const newEndIndex = Math.min(items.length, firstLowerItemIndex + itemCountBelowViewPort - 1)

        //newEndIndex - sliceSize = index of first invisible index above painted area. so add + 1 to get the new start index
        const newStartIndex = Math.max(0, newEndIndex - sliceSize + 1);

        return {
            newStartIndex,
            newEndIndex,
        }
    }

    function calculateVirtualListBounds() {

    }

    const onComplete = () => {
        if (!rootRef.current) {
            throw new Error("Missing rootRef");
        }

        const {
            scrollTop,
            scrollHeight
        } = rootRef.current || {};

        const bottomObserverTop = (observers.current?.bottom as ItemInfo)?.top;

        logger.info("");
        logger.info("Tween onComplete called.");
        logger.info("scrollTop: " + scrollTop);
        logger.info("scrollHeight: " + scrollHeight);

        // const {listBottom, listTop} = calculateVirtualListBounds();
        if (!observers.current.bottom) {
            logger.warn("no bottom observer found after tween completed");
        }

        //@todo.. observers might not always be present. use a different calculation
        if (bottomObserverTop && scrollTop > bottomObserverTop) {
            logger.info("bottomObserverTop: " + bottomObserverTop);
            logger.info("sliceSize: " + sliceSize)
            logger.info("items.length: " + items.length)
            logger.info("startIndex: " + startIndex)

            const {newStartIndex} = calculateStartIndex();
            logger.info("Calculated new startIndex. Old startIndex: %s. New startIndex: %s. ", startIndex, newStartIndex)
            logger.info("");

            setStartIndex(newStartIndex);

            if (!overlayVisible) {
                setOverlayVisible(true)
            }
        }
    }

    const tweenRef = useRef<Tween<{ x: number }>>();

    if (tweenRef.current) {
        tweenRef.current.onComplete(onComplete)
    }

    function scrollTo(target: number, options: { duration?: number } = {}) {
        if (!rootRef.current) {
            throw new Error("tried to scroll without rootRef present");
        }
        logger.info("starting scroll tween. scrolling to " + target);
        logger.info("starting scroll tween. scrollHeight: " + rootRef.current.scrollHeight);

        if (tweenRef.current) {
            tweenRef.current.stop();
        }

        const {
            duration = 500
        } = options;

        const tween = createPropertyTween(rootRef.current, "scrollTop", target,
                {
                    duration,
                    onUpdate: () => {
                        logger.info("updating scroll tween. scrollTop: " + rootRef.current?.scrollTop);
                    },
                    onComplete
                });
        tweenRef.current = tween;
        tween.start()

    }

    //@todo make a map of items and check for reference difference. redraw based on that

    let prerenderedItems = items.filter((item, i) => !map[item.id].height);

    prerenderedItems.forEach(item => {
        heightChangeMap[item.id] = true;
    })

    function onPointerEnd() {
        if (!rootRef.current) {
            throw new Error("Missing rootRef.current during onPointerEnd call")
        }

        const {scrollHeight, scrollTop, clientHeight} = rootRef.current;
        const distanceToBottom = scrollHeight - scrollTop - clientHeight;

        if (distanceToBottom < 5) {
            setLocked(true);
        } else {
            setLocked(false)
        }
    }

    const onScrollLockedRef = useRef(false);
    const handleRef = useRef(0);
    const hasScrolledRef = useRef(false);

    const onScroll = useCallback(() => {
        if (!onScrollLockedRef.current) {
            hasScrolledRef.current = false;

            function calculateStart() {
                const observerMap = observers.current;
                const obsTop = observerMap?.top as ItemInfo;
                const obsBottom = observerMap?.bottom as ItemInfo;

                const topObserverBottom = (obsTop?.top || 0) + obsTop?.height;
                const bottomObserverTop = obsBottom?.top;

                if (!rootRef.current) {
                    logger.error("tried to calculate start with no rootRef present");
                    return;
                }
                const scrollTop = rootRef.current.scrollTop

                //viewport crossed an observation boundary;
                const topCrossed = scrollTop < topObserverBottom;
                const bottomCrossed = bottomObserverTop !== undefined && scrollTop > bottomObserverTop;
                const boundsCrossed = topCrossed || bottomCrossed;

                if (boundsCrossed) {
                    if (bottomCrossed) {
                        logger.info("");
                        logger.info("Viewport passed the bottom observer after onscroll event fired. Bottom observer top: %s", bottomObserverTop);
                        logger.info("scrollTop: %s", scrollTop);
                    } else if (topCrossed) {
                        logger.info("");
                        logger.info("Viewport passed the top observer after onscroll event fired. Top observer bottom: %s", topObserverBottom);
                        logger.info("scrollTop: %s", scrollTop);

                    }


                    if (!overlayVisible) {
                        setOverlayVisible(true)
                    }

                    const {newStartIndex, newEndIndex} = calculateStartIndex();
                    logger.info("Calculated new startIndex. Old startIndex: %s. New startIndex: %s. ", startIndex, newStartIndex);
                    logger.info("newEndIndex: %s. ", newEndIndex);
                    logger.info("Old top: %s", top);
                    logger.info("items.length %s", items.length);
                    logger.info("");
                    setStartIndex(newStartIndex);
                }

                onScrollLockedRef.current = false;

                if (hasScrolledRef.current) {
                    handleRef.current = requestAnimationFrame(calculateStart)
                }
            }

            handleRef.current = requestAnimationFrame(calculateStart);

            onScrollLockedRef.current = true;
            return () => {
                cancelAnimationFrame(handleRef.current);
                onScrollLockedRef.current = false;
            }
        } else {
            hasScrolledRef.current = true;
        }

    }, [setStartIndex, calculateStartIndex]);


    const {
        scrollTop,
        scrollHeight: rootScrollHeight
    } = rootRef.current || {};


    const obsBottom = observers.current?.bottom as ItemInfo;
    const obsTop = observers.current?.top as ItemInfo;

    const bottomObserverTop = obsBottom?.top;
    const topObserverTop = obsTop?.top;

    if (prerenderedItems.length > 0) {
        logger.trace("");
        logger.trace(`calculating heights for ${prerenderedItems.length} items. making quick render`)
        logger.trace("item indexes: [" + prerenderedItems.map(item => item.index).join(", ") + "]");
        logger.trace("");
    }

    let currentTop = top;
    const emptyArray = new Array(sliceSize).fill(0);

    useEffect(() => {
        if (rootRef.current) {
            const observer = new ResizeObserver(() => {
                onScroll()
            });
            observer.observe(rootRef.current);
            return () => observer.disconnect();
        }
    }, [rootRef.current, onScroll])

    return <div
            ref={rootRef}
            style={{
                overflow: "auto",
                width,
                ...style,
            }}
            onTouchEnd={onPointerEnd}
            onMouseUp={onPointerEnd}
            onScroll={onScroll}
            {...rest}
    >
        <Box position={"relative"}>
            {
                prerenderedItems.map((item, i) => {

                    const index = item.index
                    const key = i;

                    const style = {
                        width: "100%",
                        position: "absolute",
                        visible: false,
                        top: 0,
                    }

                    return <Wrapper
                            key={key}
                            index={index}
                            rootRef={rootRef}
                            logger={logger}
                            onHeightChanged={clientHeight => {

                                delete heightChangeMap[item.id];
                                map[item.id].height = clientHeight;

                                if (Object.keys(heightChangeMap).length === 0) {
                                    logger.info("height change map empty. finished prerender process. prerendered %s items. refreshing item positions for %s items",
                                            prerenderedItems.length,
                                            items.length
                                    )
                                    refreshItemPositions()
                                }
                            }}
                    >
                        <Children index={index} style={style}/>
                    </Wrapper>
                })
            }

        </Box>

        {/* <Zoom in={overlayVisible}> */}
        {/*   <Box */}
        {/*     zIndex={10000000} */}
        {/*     display={"flex"} */}
        {/*     padding={"20px"} */}
        {/*     position={"fixed"} */}
        {/*     width={"100%"} */}
        {/*     justifyContent={"center"} */}
        {/*     top={0} */}
        {/*     left={0} */}
        {/*   > */}
        {/*     <Paper elevation={4} style={{display: "flex", padding: "10px"}}> */}
        {/*       Changing visible elements */}
        {/*     </Paper> */}
        {/*   </Box> */}
        {/* </Zoom> */}

        <div style={{height: scrollHeight, position: "relative"}}>
            {
                emptyArray.map((_, i) => {

                    const index = startIndex + i;
                    const item = items[index];
                    const key = item.id;
                    const height = map[item.id].height;

                    const style: CSSProperties = {
                        width: "100%",
                        position: "absolute",
                        top: currentTop,
                    }

                    map[item.id].top = currentTop;

                    if (height) {
                        style["height"] = height + "px"
                    }

                    currentTop += height;

                    const isBottomObserver = i === bottomObservationIndex;
                    const isTopObserver = i === topObservationIndex;
                    if (i === emptyArray.length - 1) {
                        const bottom = currentTop;
                        logger.info("");

                        logger.info("Render cycle triggered.");
                        logger.info("Last item processed.");
                        logger.info("emptyArray.length: " + emptyArray.length);

                        logger.info("locked: " + locked);

                        logger.info("startIndex: " + startIndex);
                        logger.info("sliceSize: " + sliceSize);
                        logger.info("top: " + top);
                        logger.info("bottom: " + bottom);

                        logger.info("scrollTop: " + scrollTop);
                        logger.info("scrollHeight: " + rootScrollHeight);
                        logger.info("topObserverTop: " + topObserverTop);
                        logger.info("topObservationIndex: " + topObservationIndex);
                        logger.info("bottomObserverTop: " + bottomObserverTop);
                        logger.info("bottomObserverIndex: " + bottomObservationIndex);
                        logger.info("");
                        // window.rootRef = rootRef;
                        // window.scrollTo = scrollTo;
                        // window.refreshItemPositions = refreshItemPositions;
                        // window.items = items;
                        // window.emptyArray = emptyArray;
                        // window.map = map;
                        // const getVirtualListTop =  () => items.map((it,i) => map[i].height).slice(0, sliceSize).reduce((partialSum, a) => partialSum + a, 0);
                        // const getVirtualListHeight = () => items.map((it,i) => map[i].height).slice(startIndex,startIndex+ sliceSize).reduce((partialSum, a) => partialSum + a, 0);
                        // const getVirtualListBottom = () => getVirtualListTop() + getVirtualListHeight();
                        // window.getVirtualListHeight = getVirtualListHeight;
                        // window.getVirtualListTop = getVirtualListTop;
                        // window.getVirtualListBottom = getVirtualListBottom;
                    }
                    return <Wrapper
                            key={key}
                            index={index}
                            rootRef={rootRef}
                            observe={isBottomObserver || isTopObserver}
                            isTopObserver={isTopObserver}
                            onIntersected={isBottomObserver ? onBottomIntersection : onTopIntersection}
                            logger={logger}
                            // onHeightChanged={clientHeight => {
                            //   // delete heightChangeMap[index];
                            //   // map[index].height = clientHeight;
                            //   // if (Object.keys(heightChangeMap).length === 0) {
                            //   //   refreshItemPositions()
                            //   // }
                            // }}
                    >
                        <Children height={map[item.id].height} index={index} style={style}/>
                    </Wrapper>

                })
            }
        </div>

    </div>
}
